import type { Dispatch, SetStateAction } from 'react';

import { useEffect, useMemo, useState } from 'react';

import {
  SIZE_TRANSFER_LENGTH,
  SIZE_TRANSFER_RANGE_IS_WAIST_LENGTH,
  SIZE_TRANSFER_SIZE,
} from '~/utilities/constants/localStorageKeys';
import { usePDPDynamicContext } from '~/utilities/context/dynamic/PDPDynamicContext';
import { useSizeContext } from '~/utilities/context/dynamic/SizeContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { SizeType } from '~/utilities/graphql/codegen';
import { storageAvailable } from '~/utilities/helpers';

type Params = {
  setActivatable: Dispatch<SetStateAction<boolean>>;
  setBackInStockDialogOpen: Dispatch<SetStateAction<boolean>>;
};

export const useSizeSelectorInitialize = ({ setBackInStockDialogOpen, setActivatable }: Params) => {
  const { setSizeCode, setGridValues } = useSizeContext();
  const { product } = usePDPContext();
  const { dynamicProductData } = usePDPDynamicContext();

  const sizeInformation = dynamicProductData.sizeInformation ?? product.sizeInformation;
  const isDoubleSize = sizeInformation?.sizeType === SizeType.NumericDouble;
  const [initialized, setInitialized] = useState(false);

  const sizes = useMemo(() => sizeInformation?.sizes || [], [sizeInformation]);

  useEffect(() => {
    const isLocalStorageAvailable = storageAvailable('localStorage');
    const productSizeValue = (product.code as string).split('-')[3]?.toUpperCase();
    const isProductVariant = !!product.baseProduct && !!productSizeValue;

    const storageWaistSize: string[] = JSON.parse(
      isLocalStorageAvailable ? localStorage.getItem(SIZE_TRANSFER_SIZE) || '[]' : '[]'
    );
    const storageLengthSize: string[] = JSON.parse(
      isLocalStorageAvailable ? localStorage.getItem(SIZE_TRANSFER_LENGTH) || '[]' : '[]'
    );

    const isLocalDoubleSize: boolean = JSON.parse(
      isLocalStorageAvailable
        ? localStorage.getItem(SIZE_TRANSFER_RANGE_IS_WAIST_LENGTH) || 'false'
        : 'false'
    );

    const singleValueStorageSize = storageWaistSize.length === 1;
    const singleValueStorageLength = storageLengthSize.length === 1;

    const isLocalVariant = isDoubleSize
      ? isLocalDoubleSize
      : singleValueStorageSize && !isLocalDoubleSize;

    let gridValue1 = '';
    let gridValue2 = '';
    let shouldOpenDialog = false;
    let selectedSizeProductCode = '';

    if (isProductVariant || isLocalVariant) {
      // Getting the grid value from the local storage value set from plp to make "24 (US 00)" to "24"
      const storageGridValue1 =
        isLocalVariant && singleValueStorageSize
          ? storageWaistSize[0].toUpperCase().split(' ')[0]
          : '';
      const storageGridValue2 =
        isLocalVariant && singleValueStorageLength
          ? storageLengthSize[0].toUpperCase().split(' ')[0]
          : '';

      if (isDoubleSize) {
        gridValue1 = isProductVariant ? productSizeValue.slice(0, 2) : storageGridValue1;
        gridValue2 = isProductVariant ? productSizeValue.slice(2, 4) : storageGridValue2;

        if (gridValue1 && gridValue2) {
          shouldOpenDialog = !sizeInformation?.sizes?.find(
            size =>
              size?.gridValue1?.toUpperCase() === gridValue1 &&
              size?.gridValue2?.toUpperCase() === gridValue2
          )?.purchasable;
        }
      } else {
        const selectedSize = sizeInformation?.sizes?.find(
          size => size?.code?.toUpperCase() === product.code.toUpperCase()
        );

        gridValue1 = isProductVariant ? selectedSize?.gridValue1 ?? '' : storageGridValue1;
        gridValue2 = '';
        shouldOpenDialog = !sizeInformation?.sizes?.find(
          size => size?.gridValue1?.toUpperCase() === gridValue1
        )?.purchasable;
      }

      if (isProductVariant) {
        selectedSizeProductCode = product.code;
      } else if (isLocalVariant) {
        if (isDoubleSize) {
          selectedSizeProductCode =
            gridValue1 && gridValue2 ? `${product.code}-${gridValue1}${gridValue2}` : '';
        } else {
          selectedSizeProductCode =
            sizeInformation?.sizes?.find(size => size?.gridValue1 === gridValue1)?.code ?? '';
        }
      }

      setBackInStockDialogOpen(shouldOpenDialog);
      setActivatable(!shouldOpenDialog);
      setGridValues({ gridValue1, gridValue2 });

      if (!shouldOpenDialog) {
        setSizeCode(selectedSizeProductCode);
      }
    } else {
      setGridValues({ gridValue1: '', gridValue2: '' });
      setBackInStockDialogOpen(false);
    }

    if (product.oneSize) {
      setSizeCode(sizes?.at(0)?.code || 'PC');
    }

    setInitialized(true);
  }, [
    isDoubleSize,
    product.baseProduct,
    product.code,
    product.oneSize,
    setActivatable,
    setBackInStockDialogOpen,
    setGridValues,
    setSizeCode,
    sizeInformation?.sizes,
    sizes,
  ]);

  return {
    initialized,
  };
};
